import {connect} from 'pwa-helpers';
import {LitElement, html, css} from 'lit';
import {navigator} from 'lit-element-router';
import {customElement} from 'lit/decorators/custom-element.js';
import {property} from 'lit/decorators/property.js';
import {classMap} from 'lit/directives/class-map.js';
import {when} from 'lit/directives/when.js';
import {msg, updateWhenLocaleChanges} from '@lit/localize';
import store from '../store';
import {
	setMedia,
	setSection,
	nextSection,
	previousSection,
	setHighlightFeatures
} from '../slices/statusSlice';

import next from '../../icons/next';
import previous from '../../icons/previous';
import checkmark from '../../icons/checkmark';
import {selectCurrentSection, selectFeatureModelCompletionPercentage, selectSectionConfig, selectUnsatisfied, selectLightProfileIsSatisfied, selectLuminaryIsSatisfied} from '../selectors';
import {routeUrl} from '../util/routeUrl';

@customElement('sections-navigation')
export class SectionsNavigation extends connect(store)(navigator(LitElement)) {

	static styles = css`
	  :host {
		display: flex;
	  }
	  @media (max-width: 768px) {
		:host {
		  right:initial;
		  top:initial;
		  bottom: 0;
		  padding: var(--small-padding) 0;
		  width: 100%;
		  justify-content: center;
		  background: linear-gradient(180deg, rgba(0.3,0.3,0.3,0) 0%, rgba(0.3,0.3,0.3,0.4) 20%, rgba(0.3,0.3,0.3,0.7) 100%);
		}
	  }

	  button {
		font: var(--font-widget-label);
		color: var(--color-text-widget-title);
		height: var(--sections-progress-height);
		text-transform: uppercase;
		letter-spacing: 1px;
		transition: background-color 0.4s, color 0.4s;
		background-color: transparent;
		border: none;
		cursor: pointer;
		line-height: 0;
	  }
	  .arrows {
		display: flex;
		gap: var(--small-padding);
		align-items: flex-end;
		color: var(--color-text-sections);
		padding: 0;
		position: relative;
		z-index: 20;
	  }
	  .arrows.applicationSectionNav {
		display: block;
	  }
	  @media (max-width: 768px) {
		.arrows {
		  padding: 0;
		}
	  }

	  .arrow {
		flex-grow: 0;
		width: var(--sections-progress-height);
		height: var(--sections-progress-height);

		color: var(--color-grey4);
		transition: background-color 0.4s, color 0.4s, border-voler 0.4s;

		border: 1px solid var(--color-grey5);
		pointer-events: none;
		font-size: var(--font-size-sections);
		background-color: var(--color-background-widget);
		cursor: pointer;
	  }
	  .arrow svg {
		height: 12px;
		width: auto;
	  }
	  .arrow svg * {
		stroke: currentColor;
	  }

	  .arrow.active {
		border: 1px solid var(--color-white);
		color: var(--color-white);
		pointer-events: all;
	  }

	  .visual {
		flex-grow: 1;
		width: var(--sections-progress-height);
		height: var(--sections-progress-height);
		margin-left: var(--small-padding);

		color: var(--color-white);
		transition: background-color 0.4s, color 0.4s, border-voler 0.4s;

		border: 1px solid var(--color-white);
		font-size: var(--font-size-sections);
		background-color: transparent;
		cursor: pointer;
	  }
	  .visual svg * {
		stroke: currentColor;
	  }
	  @media (min-width: 768px) {
		.visual {
			display: none;
		}
	  }
	  .icon {
		pointer-events: none;
		width: 11px;
		height: 11px;
		display: inline-block;
		color: var(--color-white);
	  }

	  .icon svg {
		display: block;
		fill: currentColor
	  }

	  .icon.satisfied {
		color: var(--color-text-widget-title);
	  }

	  .specsheet {
		color: var(--color-grey5);
		border: 1px solid var(--color-grey5);
		padding: 0 var(--small-padding);
	  }

	  .specsheet.active {
		background-color: var(--color-grey2);
		color: var(--color-black);
		cursor: pointer;
	  }

	  .specsheet.active .badge {
		display: none;
	  }

	  .applicationSectionNav {
		position: relative;
		right: 140px;
	  }


	  .applicationSectionNav .arrows {
		margin-bottom: var(--column-gap);
	  }

	  .choice {
		border: 1px solid var(--color-white);
		text-transform: none;
		font: var(--font-summary-table);
		padding: 0 var(--very-small-padding);
	  }

	  .choice svg {
		padding-left: var(--very-small-padding);
	  }

	  button:disabled, button[disabled] {
		border: 1px solid var(--color-grey5);
		color: var(	--color-grey5);
		cursor: default;
	  }

	  button[disabled] svg {
		stroke: var(	--color-grey5);
		cursor: default;
	  }

	  @media (min-width: 768px) {
		.specsheet {
		  display: none;
		}
	  }

	  .badge {
		background-color: var(--color-warn);
		color: var(--color-white);
		border-radius: 5px;
		font-size: 12px;
		padding: 1px 4px;
		pointer-events: none;
	  }

	  .badge.satisfied {
		background-color: transparent
	  }

	  @media (max-width: 768px) {
		.arrows {
			position: absolute;
			right: var(--small-padding);
			bottom: var(--small-padding);
		}
	  }
	`;

	@property({type: String})
	currentSection = '';

	constructor() {
		super();
		updateWhenLocaleChanges(this);
		store.subscribe(() => this.requestUpdate());
	}

	connectedCallback() {
		super.connectedCallback();
		// addEventListener('keydown', this.handleKeydown);
	}

	stateChanged(state) {
		this.state = state;
		this.productData = state.productData.productData;

		this.lang = state.status.lang;
		this.sectionConfig = selectSectionConfig(state);
		this.currentSection = selectCurrentSection(state);
		this.dashboardActive = state.status.dashboardActive;
		this.specsheetActive = state.status.specsheetActive;
		this.visualActive = state.status.visualActive;
		this.features = state.featureModel.data.features ?? [];
		this.decisions = state.decisions.data;
		this.featureModelIsSatisfied = state.decisions.data.isSatisfied;
	}

	setSection(e) {
		store.dispatch(setSection({currentSection: e.target.getAttribute('key')}));
		store.dispatch(setMedia({media: 'sections'}));
	}

	showSpecsheet(currentIndex) {
		return currentIndex === this.sectionConfig.length - 1 && this.featureModelIsSatisfied;
	}
	previousSection() {
		const currentIndex = this.sectionConfig.findIndex(section => section.id === this.currentSection);
		const newIndex = currentIndex > 0 ? currentIndex - 1 : 0;
		const newSection = this.sectionConfig[newIndex].id;

		store.dispatch(setSection({currentSection: newSection}));
		store.dispatch(setMedia({media: 'sections'}));
	}
	nextSection() {
		const currentIndex = this.sectionConfig.findIndex(section => section.id === this.currentSection);
		const newIndex = currentIndex < this.sectionConfig.length - 1 ? currentIndex + 1 : this.sectionConfig.length - 1;
		const newSection = this.sectionConfig[newIndex].id;

		if (currentIndex < this.sectionConfig.length - 1) {
			store.dispatch(setSection({currentSection: newSection}));
			store.dispatch(setMedia({media: 'sections'}));
		}
	}
	openSpecsheet(e) {
		if (this.featureModelIsSatisfied) {
			if (this.state.status.family === 'LightProfile') {
				// TODO: not hardcoded configuration of summary/specsheet component
				this.navigate(routeUrl({media: this.state.status.media === 'summary' ? 'sections' : 'summary'}));
			} else {
				this.navigate(routeUrl({media: 'specsheet'}));
			}
		} else {
			const unsatisfied = selectUnsatisfied(this.state);
			if (unsatisfied && unsatisfied.length > 0) {
				store.dispatch(setSection({ currentSection: unsatisfied[0].sectionId}));
				store.dispatch(setHighlightFeatures({ highlightFeatures: unsatisfied.reduce( (acc, s) => acc.concat(s.unsatisfiedFeatures), [])}));
			}
		}
	}
	closeSpecsheet(e) {
		this.navigate(routeUrl({media: this.forceOverview ? 'overview' : 'sections'}));
	}

	toggleSpecsheet(e) {
		if (this.featureModelIsSatisfied) {
			if (this.specsheetActive) {
				this.navigate(routeUrl({media: this.forceOverview ? 'overview' : 'sections'}));
			} else {
				if (this.state.status.family === 'LightProfile') {
					// TODO: not hardcoded configuration of summary/specsheet component
					this.navigate(routeUrl({media: this.state.status.media === 'summary' ? 'sections' : 'summary'}));
				} else {
					this.navigate(routeUrl({media: 'specsheet'}));
				}
			}
		} else {
			if (this.state.status.family === 'LightProfile') {
				// TODO: not hardcoded configuration of summary/specsheet component
				this.navigate(routeUrl({media: this.state.status.media === 'summary' ? 'sections' : 'summary'}));
			} else {
				const unsatisfied = selectUnsatisfied(this.state);
				if (unsatisfied && unsatisfied.length > 0) {
					store.dispatch(setSection({currentSection: unsatisfied[0].sectionId}));
					store.dispatch(setHighlightFeatures({highlightFeatures: unsatisfied.reduce((acc, s) => acc.concat(s.unsatisfiedFeatures), [])}));
				}
			}
		}
	}

	handleKeydown = (e) => {
		switch (e.keyCode) {
			case 39: // cursor right
				e.preventDefault();
				this.nextSection();
				break;
			case 37:  // cursor left
				e.preventDefault();
				this.previousSection();
				break;
		}
	};

	render() {
		if (this.dashboardActive || this.specsheetActive) return null;
		const currentIndex = this.sectionConfig.findIndex(section => section.id === this.currentSection);
		const isActive = selectLightProfileIsSatisfied(this.state);
		const isButtonDisabled = !isActive;
		return html`
			<div class="arrows">
				<toolbar-button .icon=${previous} .disabled=${currentIndex === 0} .action=${this.previousSection.bind(this)}></toolbar-button>
				${when(this.specsheetActive,
						() => html`
							<toolbar-button .icon=${close} .action=${this.closeSpecsheet.bind(this)}></toolbar-button>`,
						() => html`
						<!--<button @click="${this.openSpecsheet}" class="specsheet ${classMap({active: this.featureModelIsSatisfied})}">
							${msg('Specsheet')}
							<span class="badge ${classMap({satisfied: this.featureModelIsSatisfied})}">
								${selectFeatureModelCompletionPercentage(this.state)} %
							</span>
						</button>!-->
					`)
				}
				<toolbar-button .icon=${next}
								.label=${msg('Next')}
								.disabled=${!(currentIndex < this.sectionConfig.length - 1 || this.featureModelIsSatisfied)}
								.action=${this.nextSection.bind(this)}></toolbar-button>
			</div>
		`;
	}
}
